import { useMemo } from "react";
import type { DenseRouteCoordinate } from "../../../../../../store/admin/routesStore";
import type { ThemeClasses, RoutePoint } from "../types";
import MapPicker from "../../../../../../components/base/MapPicker";

export interface RouteMapPreviewProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  isEdit: boolean;
  routePoints: RoutePoint[];
  routeDistance: number;
  estimatedTime: string;
  firstPointRadius: number;
  lastPointRadius: number;
  onPointsChange: (newPoints: { lat: number; lng: number }[]) => void;
  onRouteCalculated: (distance: number) => void;
  onFirstPointRadiusChange?: (radius: number) => void;
  onLastPointRadiusChange?: (radius: number) => void;
  corridorBuffer: number;
  storedAxis?: DenseRouteCoordinate[];
  onAxisCalculated?: (path: { lat: number; lng: number }[]) => void;
  readOnly?: boolean;
}

export function RouteMapPreview({
  themeClasses,
  isEdit,
  routePoints,
  routeDistance,
  estimatedTime,
  firstPointRadius,
  lastPointRadius,
  onPointsChange,
  onRouteCalculated,
  onFirstPointRadiusChange,
  onLastPointRadiusChange,
  corridorBuffer,
  storedAxis,
  onAxisCalculated,
  readOnly = false,
}: RouteMapPreviewProps) {
  const axis =
    readOnly && Array.isArray(storedAxis) && storedAxis.length >= 2
      ? storedAxis.map((p) => ({
          lat: Number(p.latitude),
          lng: Number(p.longitude),
        }))
      : undefined;

  const coordinates = useMemo(
    () =>
      routePoints.map((p) => ({
        lat: Number(p.latitude),
        lng: Number(p.longitude),
      })),
    [routePoints]
  );

  const originPoint = routePoints[0];
  const destPoint = routePoints[routePoints.length - 1];

  const memoizedOriginCoords = useMemo(() => ({
    lat: Number.isFinite(originPoint?.latitude) ? originPoint.latitude : 19.4326,
    lng: Number.isFinite(originPoint?.longitude) ? originPoint.longitude : -99.1332,
  }), [originPoint?.latitude, originPoint?.longitude]);

  return (
    <div
      className={`w-full h-full flex flex-col gap-3 bg-app p-4 2xl:p-6`}
    >
      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={`text-sm font-semibold ${themeClasses.text}`}
        >
          Vista previa de la ruta
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <span className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}>
            <span className="w-2.5 h-2.5 rounded-full bg-success inline-block" />
            Origen
          </span>
          {routePoints.length > 2 && (
            <span className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}>
              <span className="w-2.5 h-2.5 rounded-full bg-warning inline-block" />
              Vía
            </span>
          )}
          <span className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}>
            <span className="w-2.5 h-2.5 rounded-full bg-error inline-block" />
            Destino
          </span>
          {corridorBuffer > 0 && (
            <span
              className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}
              title="Salir de esta franja es lo que dispara la alerta de desvío"
            >
              <span className="w-3.5 h-2.5 rounded-sm inline-block border border-fuchsia-600 bg-fuchsia-200/60" />
              Corredor
            </span>
          )}
          {(firstPointRadius > 0 || lastPointRadius > 0) && (
            <span
              className={`flex items-center gap-1 text-xs ${themeClasses.textSecondary}`}
              title="Zonas que marcan el inicio y el fin del viaje"
            >
              <span className="w-2.5 h-2.5 rounded-full inline-block border border-success bg-success/20" />
              Zonas
            </span>
          )}
        </div>
      </div>

      <MapPicker
        shape="linear"
        type="route"
        coordinates={coordinates}
        onPointsChange={readOnly ? undefined : onPointsChange}
        onRouteCalculated={readOnly ? undefined : onRouteCalculated}
        estimatedTime={estimatedTime}
        polygonArea={0}
        routeDistance={routeDistance}
        lat={memoizedOriginCoords.lat}
        lng={memoizedOriginCoords.lng}
        radius={0}
        isEdit={readOnly ? false : isEdit}
        firstPointRadius={firstPointRadius}
        lastPointRadius={lastPointRadius}
        onFirstPointRadiusChange={readOnly ? undefined : onFirstPointRadiusChange}
        onLastPointRadiusChange={readOnly ? undefined : onLastPointRadiusChange}
        corridorBuffer={corridorBuffer}
        storedAxis={axis}
        onAxisCalculated={readOnly ? undefined : onAxisCalculated}
      />

      {/* Route summary */}
      {!readOnly && originPoint && destPoint && (
        <div
          className={`grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs rounded-lg p-3 border ${themeClasses.cardBorder} bg-card`}
        >
          <div>
            <span className="text-content-faint block">Distancia</span>
            <span className={`font-semibold ${themeClasses.text}`}>
              {routeDistance > 0 ? `${routeDistance.toFixed(1)} km` : "—"}
            </span>
          </div>
          <div>
            <span className="text-content-faint block">Tiempo estimado</span>
            <span className={`font-semibold ${themeClasses.text}`}>
              {estimatedTime || "—"}
            </span>
          </div>
          <div>
            <span className="text-content-faint block">Puntos</span>
            <span className={`font-semibold ${themeClasses.text}`}>
              {routePoints.length}{" "}
              <span className="font-normal text-content-faint">
                ({routePoints.length - 2} int.)
              </span>
            </span>
          </div>
          <div>
            <span className="text-content-faint block">Corredor</span>
            <span className={`font-semibold ${themeClasses.text}`}>
              {corridorBuffer > 0 ? `${corridorBuffer * 2} m` : "—"}
            </span>
          </div>
        </div>
      )}

      {!readOnly && (
        <p className="text-[11px] text-content-faint leading-relaxed">
          Arrastra los marcadores para ajustar la ruta. Los cambios en la tabla de puntos también se reflejan aquí.
        </p>
      )}
    </div>
  );
}
